import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { getAsset } from '../../services/assetService';
import { getAssetInspections } from '../../services/inspectionService';
import { Asset, Inspection as SharedInspection } from '@easydone/shared';
import {
  ClipboardDocumentCheckIcon,
  PlusIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  WrenchScrewdriverIcon,
  PlayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

export function AssetInspectionHistoryPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const { currentOrg } = useOrg();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [inspections, setInspections] = useState<SharedInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usedTemplates, setUsedTemplates] = useState<Array<{ id: string; name: string; count: number }>>([]);

  useEffect(() => {
    loadData();
  }, [assetId, currentOrg]);

  const loadData = async () => {
    if (!assetId || !currentOrg) return;

    setLoading(true);
    setError(null);
    console.log('Loading asset with ID:', assetId, 'for org:', currentOrg.id);

    try {
      const [assetData, inspectionsData] = await Promise.all([
        getAsset(currentOrg.id, assetId),
        getAssetInspections(currentOrg.id, assetId, 10),
      ]);

      console.log('Asset data:', assetData);
      console.log('Inspections data:', inspectionsData);

      if (!assetData) {
        setError(`Asset not found with ID: ${assetId}. Please verify the QR code is correct.`);
      }

      setAsset(assetData);
      setInspections(inspectionsData as any);

      // Extract unique templates from inspections
      const templateMap = new Map<string, { id: string; name: string; count: number }>();
      inspectionsData.forEach((inspection: any) => {
        const templateId = inspection.templateId;
        const templateName = inspection.templateName || 'Unknown Template';

        if (templateMap.has(templateId)) {
          const existing = templateMap.get(templateId)!;
          templateMap.set(templateId, { ...existing, count: existing.count + 1 });
        } else {
          templateMap.set(templateId, { id: templateId, name: templateName, count: 1 });
        }
      });

      setUsedTemplates(Array.from(templateMap.values()));
    } catch (err: any) {
      console.error('Failed to load asset data:', err);
      setError(err?.message || 'Failed to load asset data. Please check your permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewInspection = () => {
    navigate(`/inspections/new?assetId=${assetId}`);
  };

  const handleInspectNow = (templateId: string) => {
    if (!asset) return;
    const params: Record<string, string> = {
      assetId: asset.id,
      templateId: templateId,
      inspectNow: 'true',
    };
    // Add flocId if asset is commissioned
    if (asset.currentFlocId) {
      params.flocId = asset.currentFlocId;
    }
    navigate(`/inspections/new?${new URLSearchParams(params).toString()}`);
  };

  const handleViewInspection = (inspectionId: string) => {
    navigate(`/inspections/${inspectionId}`);
  };

  const handleCreateFromPrevious = (inspection: SharedInspection) => {
    // Navigate to new inspection with data from previous
    const params: Record<string, string> = {
      templateId: inspection.templateId,
      copyFromId: inspection.id,
    };
    // Add assetId if available
    if (inspection.assetId) {
      params.assetId = inspection.assetId;
    }
    // Add flocId if available
    if (inspection.flocId) {
      params.flocId = inspection.flocId;
    }
    const urlParams = new URLSearchParams(params);
    navigate(`/inspections/new?${urlParams.toString()}`);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getResultBadge = (result?: string) => {
    if (!result) return null;
    const colors = {
      pass: 'bg-green-500',
      fail: 'bg-red-500',
      partial: 'bg-yellow-500',
    };
    return (
      <span className={`inline-block w-3 h-3 rounded-full ${colors[result as keyof typeof colors] || 'bg-gray-500'}`} />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Asset Not Found</h2>
          <p className="text-red-700 mb-4">{error || 'The requested asset could not be found.'}</p>
          <div className="space-y-2 text-sm text-red-600">
            <p><strong>Troubleshooting:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Verify that the QR code belongs to your organization</li>
              <li>Check that the asset still exists in the system</li>
              <li>Ensure you have permission to view this asset</li>
              <li>Asset ID: <code className="bg-red-100 px-1 rounded">{assetId}</code></li>
            </ul>
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/assets')}
              className="btn btn-secondary"
            >
              Go to Assets List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const lastInspection = inspections[0];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Asset Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{asset.name}</h1>
            <p className="text-sm text-gray-600 mt-1">Asset ID: {asset.assetId}</p>
            {asset.type && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                {asset.type}
              </span>
            )}
          </div>
        </div>

        {asset.description && (
          <p className="text-gray-700 text-sm mb-4">{asset.description}</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          {asset.manufacturer && (
            <div>
              <span className="text-gray-600">Manufacturer:</span>
              <span className="ml-2 font-medium">{asset.manufacturer}</span>
            </div>
          )}
          {asset.model && (
            <div>
              <span className="text-gray-600">Model:</span>
              <span className="ml-2 font-medium">{asset.model}</span>
            </div>
          )}
          {asset.serialNumber && (
            <div>
              <span className="text-gray-600">Serial:</span>
              <span className="ml-2 font-medium">{asset.serialNumber}</span>
            </div>
          )}
          {asset.criticality && (
            <div>
              <span className="text-gray-600">Criticality:</span>
              <span className={`ml-2 font-medium ${
                asset.criticality === 'critical' ? 'text-red-600' :
                asset.criticality === 'high' ? 'text-orange-600' :
                asset.criticality === 'medium' ? 'text-yellow-600' : 'text-green-600'
              }`}>{asset.criticality.toUpperCase()}</span>
            </div>
          )}
        </div>
      </div>

      {/* Template History - Quick Access to Common Templates */}
      {usedTemplates.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <DocumentTextIcon className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Templates Used for This Asset
            </h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Quick access to inspection templates previously used for this asset
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {usedTemplates.map((template) => (
              <div
                key={template.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{template.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Used {template.count} time{template.count !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => handleInspectNow(template.id)}
                    className="btn btn-primary text-xs sm:text-sm py-1.5 px-3 flex items-center whitespace-nowrap"
                  >
                    <PlayIcon className="w-4 h-4 mr-1" />
                    Inspect Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={handleNewInspection}
          className="btn btn-primary flex-1 flex items-center justify-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Inspection
        </button>
        {lastInspection && (
          <button
            onClick={() => handleCreateFromPrevious(lastInspection)}
            className="btn btn-secondary flex-1 flex items-center justify-center"
          >
            <DocumentDuplicateIcon className="w-5 h-5 mr-2" />
            Re-inspect (Copy Last)
          </button>
        )}
      </div>

      {/* Recent Inspections */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <ClipboardDocumentCheckIcon className="w-5 h-5 mr-2" />
            Recent Inspections ({inspections.length})
          </h2>
          <p className="text-sm text-gray-600 mt-1">Last 10 inspections for field review</p>
        </div>

        {inspections.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <ClipboardDocumentCheckIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No inspections yet</p>
            <p className="text-sm mt-1">Start your first inspection for this asset</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {inspections.map((inspection) => (
              <div key={inspection.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {getResultBadge(inspection.result)}
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(inspection.status)}`}>
                        {inspection.status.replace('_', ' ')}
                      </span>
                      {inspection.maintenanceRequired && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <WrenchScrewdriverIcon className="w-3 h-3 mr-1" />
                          Maintenance
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-900 font-medium truncate">
                      {formatDate(inspection.completedAt || inspection.createdAt)}
                    </p>
                    {inspection.inspectorName && (
                      <p className="text-xs text-gray-600 mt-1">By: {inspection.inspectorName}</p>
                    )}
                    {inspection.maintenanceRequired && inspection.maintenanceUrgency && (
                      <p className="text-xs text-red-600 mt-1 font-medium">
                        Urgency: {inspection.maintenanceUrgency.toUpperCase()}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => handleViewInspection(inspection.id)}
                      className="btn btn-secondary text-xs sm:text-sm py-1.5 px-3"
                    >
                      <EyeIcon className="w-4 h-4 mr-1" />
                      View
                    </button>
                    <button
                      onClick={() => handleCreateFromPrevious(inspection)}
                      className="btn btn-secondary text-xs sm:text-sm py-1.5 px-3"
                      title="Create new inspection based on this one"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4 mr-1" />
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
